import type React from "react"

import { cn } from "@coval/ui/cn"

/**
 * The keys that reach a sidebar row's destination, drawn on the row itself.
 *
 * The row is already the name of where it goes, so the keys need no sentence
 * around them: they ride at the row's trailing edge, appearing with the rest of
 * the row's hover affordances and taking no space from the pointer's path. That
 * is the whole reason they are here rather than in a tooltip — a tip that opens
 * beside the rail covers the page while the user is only passing through it.
 *
 * Shortcut keys are stored the way the keyboard reports them (`"g t"`), and
 * shown the way a key is drawn on a keyboard, so the caps uppercase here rather
 * than at the registry.
 *
 * These chords are sequences: the handler buffers `g` and matches the shortcut
 * on the `t` that follows it, so the caps are set side by side and never joined
 * by a `+`, which is how a keyboard shortcut says its keys are held together.
 */
const ShortcutKeyCap = ({ children }: { children: React.ReactNode }) => (
  // The sidebar's own fill, which on a hovered row reads as the panel showing
  // through rather than a chip laid on top — the keys stay quieter than the
  // destination they belong to. No stroke: a cap this small reads as its
  // letter, and a border would only cost width in a rail that has none to
  // spare.
  //
  // `min-w-4` with centred text keeps every cap the same width whether its
  // glyph is an `i` or a `w`, so a column of rows does not ripple.
  <kbd className="inline-flex min-w-4 justify-center rounded bg-surface-contrast px-1 py-0.5 font-mono text-[10px] font-semibold uppercase leading-none text-coval-text">
    {children}
  </kbd>
)

/**
 * The chord as `aria-keyshortcuts` spells it — the keys uppercased, in the
 * order they are pressed. A row that shows the caps sets this on the element
 * the keys actually reach, so the chord is announced as a shortcut rather than
 * folded into the link's name.
 */
export const toAriaKeyShortcuts = (shortcutKey: string) =>
  shortcutKey.toUpperCase()

export const SidebarNavShortcutKeys = ({
  shortcutKey,
  className,
}: {
  /** A registry key, e.g. `"g t"` — a chord whose keys are pressed in turn. */
  shortcutKey: string
  className?: string
}) => {
  const keys = shortcutKey.split(" ").filter(Boolean)

  if (keys.length === 0) {
    return null
  }

  return (
    // Shown with the row's other hover affordances and gone the rest of the
    // time, so a rail at rest is a list of destinations rather than a list of
    // destinations and their keys. Laid out only while shown rather than held
    // at zero opacity: the rail is 178px wide, and a chord kept in the flow
    // would take that width from the row's label on every row, at rest, to
    // spend it on keys nobody is looking for yet. The label gives the width
    // back by truncating for as long as the keys are up.
    //
    // Hidden from assistive tech: the caps sit inside the row's own link, where
    // any text of theirs would be read as part of where the link goes. The row
    // carries `aria-keyshortcuts` instead, which is the chord's own attribute
    // and leaves the link's name the destination.
    <span
      aria-hidden="true"
      className={cn(
        "hidden shrink-0 items-center gap-1",
        "group-hover:flex group-focus-within:flex",
        className,
      )}
    >
      {keys.map((key, index) => (
        // The keys of a chord are its own identity — a chord never repeats a
        // key in a way that would make the pair ambiguous — and the list is
        // fixed for the lifetime of the row, so the position is a stable key.
        <ShortcutKeyCap key={`${key}-${index}`}>{key}</ShortcutKeyCap>
      ))}
    </span>
  )
}
